import i18n from '@/i18n'
import { toast } from '@/lib/hooks/useToast'
import type { Metadata } from '@/lib/types/bgSyncTypes'

/**
 * Translate a queue-failure `Metadata` into a user-friendly destructive toast.
 *
 * Why this exists: the queue used to bubble its raw error string
 * ("HTTP 403: Forbidden") straight to a generic `showErrorToast`, which
 * rendered a toast titled "🚨 Coś poszło nie tak" with a technical
 * English description. Testers reading the staging logs reported that
 * pair as "two toasts" — the title without context plus the cryptic
 * status code — and asked for something they could act on. This helper
 * now:
 *
 *   - Maps the HTTP status (`meta.status`) to a domain-appropriate
 *     title + description (403 → "Brak uprawnień", 5xx → "Błąd serwera").
 *   - Falls back to a server-provided detail when present
 *     (`meta.errorDetail`, populated by `nativeQueueManager` from the
 *     Hydra/JSON-LD response body) so a custom backend message still
 *     reaches the user.
 *   - Picks an endpoint-tailored description for /media_objects POST
 *     so an attachment upload denial reads as "no permission to add
 *     attachments to this issue" instead of the generic permission text.
 *
 * Kept platform-agnostic — the same helper handles desktop web, Android
 * web, and iOS native queue failures because they all flow through the
 * same `Metadata` shape.
 */
export function toastFromQueueError(meta: Metadata): void {
  const t = i18n.t.bind(i18n)
  const status = typeof meta.status === 'number' ? meta.status : undefined
  const isMediaUpload = meta.type === 'media_objects' && meta.method === 'POST'

  if (status === 403 || status === 401) {
    toast({
      variant: 'destructive',
      title: t('error.forbiddenTitle'),
      description: isMediaUpload
        ? t('error.uploadForbiddenDescription')
        : (meta.errorDetail ?? t('error.forbiddenDescription')),
    })
    return
  }

  if (typeof status === 'number' && status >= 500 && status < 600) {
    toast({
      variant: 'destructive',
      title: t('error.serverErrorTitle'),
      description: meta.errorDetail ?? t('error.serverErrorDescription'),
    })
    return
  }

  // 4xx other than 401/403, or no status (e.g. network give-up after
  // MAX_RETRIES). Generic destructive toast but with a useful description
  // — never just a bare title.
  toast({
    variant: 'destructive',
    title: t('common.error'),
    description:
      meta.errorDetail ??
      (isMediaUpload ? t('error.uploadFailedDescription') : t('error.tryAgain')),
  })
}
